import { useEffect, useState } from "react";
import { http, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowClockwise, DownloadSimple, MagnifyingGlass, ShieldCheck, Info } from "@phosphor-icons/react";

// Format a Date as the value a native <input type="datetime-local"> expects,
// but expressed in UTC so it matches the table header ("Timestamp (UTC)").
// Using local time here would mislead operators — they'd type "07:00" thinking
// UTC, and the range they see wouldn't line up with the rows they see.
const toDatetimeLocalUTC = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}T${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
};

export default function NatLogs() {
  const [q, setQ] = useState({ public_ip: "", public_port: "", private_ip: "", remote_ip: "", ts_from: "", ts_to: "" });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cfg, setCfg] = useState({ retention_days: 90, syslog_port: 5140, total_records: 0, latest_ts: null });

  const loadCfg = async () => {
    try {
      const { data } = await http.get("/nat-logs/config");
      setCfg(data);
    } catch { /* silent */ }
  };

  const search = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(q).forEach(([k, v]) => { if (v !== "" && v !== null) params.set(k, v); });
      params.set("limit", "500");
      const { data } = await http.get(`/nat-logs/search?${params.toString()}`);
      setRows(data.results || []);
      toast.success(`${data.count} record${data.count === 1 ? "" : "s"} matched`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally { setLoading(false); }
  };

  // One CSV downloader shared by the top-right "Export CSV" and every quick-range
  // shortcut button. `overrideRange` lets the shortcuts skip the search form.
  const downloadCsv = async (overrideRange = null) => {
    try {
      const params = new URLSearchParams();
      const src = overrideRange || q;
      Object.entries(src).forEach(([k, v]) => { if (v !== "" && v !== null) params.set(k, v); });
      const { data } = await http.get(`/nat-logs/export.csv?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `nat-logs-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Export failed"); }
  };

  // Quick ranges use UTC boundaries because the stored logs are UTC.
  const quickRange = (rangeKey) => {
    const now = new Date();
    let from = null, to = new Date(now.getTime());
    if (rangeKey === "1h") from = new Date(now.getTime() - 3600 * 1000);
    else if (rangeKey === "24h") from = new Date(now.getTime() - 86400 * 1000);
    else if (rangeKey === "7d") from = new Date(now.getTime() - 7 * 86400 * 1000);
    else if (rangeKey === "today") from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0));
    return { ts_from: from ? toDatetimeLocalUTC(from) : "", ts_to: to ? toDatetimeLocalUTC(to) : "" };
  };

  const applyQuickRangeAndSearch = (rangeKey) => {
    const r = quickRange(rangeKey);
    setQ((prev) => ({ ...prev, ...r }));
    // fire search with the new range without waiting for state to flush
    setTimeout(() => {
      const params = new URLSearchParams();
      Object.entries({ ...q, ...r }).forEach(([k, v]) => { if (v !== "" && v !== null) params.set(k, v); });
      params.set("limit", "500");
      http.get(`/nat-logs/search?${params.toString()}`).then(({ data }) => {
        setRows(data.results || []);
        toast.success(`${data.count} record${data.count === 1 ? "" : "s"} matched (${rangeKey.toUpperCase()})`);
      }).catch((e) => toast.error(e?.response?.data?.detail || "Search failed"));
    }, 30);
  };

  const quickDownload = (rangeKey) => downloadCsv(quickRange(rangeKey));

  useEffect(() => { loadCfg(); }, []);

  return (
    <div className="space-y-6 fade-up" data-testid="natlogs-view">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="uppercase-label">CGNAT · Compliance</div>
          <h1 className="font-display font-bold text-4xl mt-1">User CTS / NAT logs</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Every private → public IP+port translation shipped from your BNGs via syslog. Query by public IP+port to answer lawful-intercept requests.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={loadCfg} className="rounded-sm border-border" data-testid="natlogs-refresh-cfg"><ArrowClockwise size={14} className="mr-2" /> Refresh</Button>
          <Button onClick={() => downloadCsv()} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="natlogs-csv"><DownloadSimple size={14} className="mr-2" /> Export CSV</Button>
        </div>
      </div>

      {/* Quick CSV download shortcuts — the compliance-team workflow is
          "give me last 24h" 99% of the time; keep it one click, not
          three form fills + submit. */}
      <div className="border border-border bg-card p-3 rounded-sm flex items-center gap-2 flex-wrap" data-testid="natlogs-quick">
        <div className="uppercase-label mr-2 flex items-center gap-2"><DownloadSimple size={12} /> Quick download</div>
        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => quickDownload("1h")} data-testid="quick-csv-1h">Last 1 hour</Button>
        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => quickDownload("today")} data-testid="quick-csv-today">Today (UTC)</Button>
        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => quickDownload("24h")} data-testid="quick-csv-24h">Last 24 hours</Button>
        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => quickDownload("7d")} data-testid="quick-csv-7d">Last 7 days</Button>
        <div className="w-px h-6 bg-border mx-2" />
        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => applyQuickRangeAndSearch("1h")} data-testid="quick-view-1h">View last 1h</Button>
        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => applyQuickRangeAndSearch("24h")} data-testid="quick-view-24h">View last 24h</Button>
        <span className="text-[11px] text-muted-foreground ml-auto">Total records: <b className="text-foreground">{cfg.total_records?.toLocaleString?.() || cfg.total_records || 0}</b> · Retention: <b className="text-foreground">{cfg.retention_days} d</b> · Latest: <b className="text-foreground">{cfg.latest_ts ? fmtDate(cfg.latest_ts) : "—"}</b></span>
      </div>

      {/* Search form */}
      <div className="border border-border bg-card p-4 rounded-sm">
        <div className="uppercase-label mb-3">Query translations</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <FormCell label="Public IP"><Input placeholder="103.135.70.158" value={q.public_ip} onChange={(e) => setQ({ ...q, public_ip: e.target.value })} className="font-mono" data-testid="q-public-ip" /></FormCell>
          <FormCell label="Public port"><Input placeholder="8123" type="number" value={q.public_port} onChange={(e) => setQ({ ...q, public_port: e.target.value })} className="font-mono" data-testid="q-public-port" /></FormCell>
          <FormCell label="Private IP"><Input placeholder="10.0.4.3" value={q.private_ip} onChange={(e) => setQ({ ...q, private_ip: e.target.value })} className="font-mono" data-testid="q-private-ip" /></FormCell>
          <FormCell label="Remote IP"><Input placeholder="1.1.1.1" value={q.remote_ip} onChange={(e) => setQ({ ...q, remote_ip: e.target.value })} className="font-mono" data-testid="q-remote-ip" /></FormCell>
          <FormCell label="From (UTC)"><Input type="datetime-local" value={q.ts_from} onChange={(e) => setQ({ ...q, ts_from: e.target.value })} className="font-mono text-xs" data-testid="q-from" /></FormCell>
          <FormCell label="To (UTC)"><Input type="datetime-local" value={q.ts_to} onChange={(e) => setQ({ ...q, ts_to: e.target.value })} className="font-mono text-xs" data-testid="q-to" /></FormCell>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Button onClick={search} disabled={loading} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="natlogs-search"><MagnifyingGlass size={14} className="mr-2" /> {loading ? "Searching…" : "Search"}</Button>
          <Button variant="outline" onClick={() => setQ({ public_ip: "", public_port: "", private_ip: "", remote_ip: "", ts_from: "", ts_to: "" })} className="rounded-sm border-border" data-testid="natlogs-clear">Clear</Button>
          <Button variant="outline" onClick={() => downloadCsv()} className="rounded-sm border-border ml-auto" data-testid="natlogs-csv-filtered"><DownloadSimple size={14} className="mr-2" /> Download filtered as CSV</Button>
        </div>
      </div>

      {/* Results */}
      <div className="border border-border bg-card overflow-x-auto rounded-sm">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border">
            <th className="uppercase-label text-left py-2 px-3">Timestamp (UTC)</th>
            <th className="uppercase-label text-left py-2 px-3">Private</th>
            <th className="uppercase-label text-left py-2 px-3">Public</th>
            <th className="uppercase-label text-left py-2 px-3">Remote</th>
            <th className="uppercase-label text-left py-2 px-3">Proto</th>
            <th className="uppercase-label text-left py-2 px-3">Source BNG</th>
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-border/60 hover:bg-accent/30" data-testid={`natlogs-row-${i}`}>
                <td className="py-2 px-3 font-mono text-xs">{fmtDate(r.ts)}</td>
                <td className="py-2 px-3 font-mono text-xs">{r.private_ip}:{r.private_port}</td>
                <td className="py-2 px-3 font-mono text-xs text-primary">{r.public_ip}:{r.public_port}</td>
                <td className="py-2 px-3 font-mono text-xs text-muted-foreground">{r.remote_ip}:{r.remote_port}</td>
                <td className="py-2 px-3 font-mono text-xs">{r.protocol || "—"}</td>
                <td className="py-2 px-3 font-mono text-xs text-muted-foreground">{r.src_ip}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-muted-foreground text-sm">Run a search or hit a quick-download button above.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FormCell({ label, children }) {
  return (
    <div>
      <Label className="uppercase-label">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
